/**
 * Lightweight REST client for the Skills + Knowledge admin surfaces.
 *
 * Separate from ChatClient (which is chat-stream-focused) to keep the
 * dependency graph clean: the admin pages don't need the SSE plumbing,
 * and the chat panel doesn't need the admin endpoints. Both auth the
 * same way: the HttpOnly `egpt_session` cookie (BFF flow), via `apiFetch`
 * which handles credentials + silent refresh + login redirect.
 */

import { apiFetch } from "@/auth/token";
import { friendlyError } from "./friendlyError";

export interface RestClientOptions {
  apiUrl: string;
  /** Inject a custom fetch (tests). Defaults to the cookie-aware `apiFetch`. */
  fetch?: typeof globalThis.fetch;
}

export class RestClient {
  private apiUrl: string;
  private fetchImpl: typeof globalThis.fetch;

  constructor(opts: RestClientOptions) {
    this.apiUrl = opts.apiUrl.replace(/\/+$/, "");
    this.fetchImpl = opts.fetch ?? apiFetch;
  }

  private async request(
    path: string,
    init: RequestInit = {},
  ): Promise<Response> {
    const url = `${this.apiUrl}${path}`;
    const resp = await this.fetchImpl(url, {
      ...init,
      credentials: "include",
      headers: {
        ...(init.headers ?? {}),
      },
    });
    if (!resp.ok) {
      // 402 subscription_inactive → redirect to /reactivate (mid-session lapse)
      if (resp.status === 402) {
        try {
          const body = await resp.clone().json();
          if (body?.code === "subscription_inactive") {
            window.location.assign("/reactivate");
            // Still throw so the caller doesn't proceed with stale data
            throw new RestError(402, "subscription_inactive", path);
          }
        } catch (e) {
          if (e instanceof RestError) throw e;
          // Not JSON or no code field — fall through to normal error handling
        }
      }
      // Try to surface the backend error detail; fall back to status text.
      // `detail` may be a human string OR a structured object (e.g. the
      // connector-OAuth path returns `{error, fallback: "manual_oauth"}` on a
      // non-discoverable server). Keep both: a display string + the raw payload
      // so friendlyError can render a fine-grained message and callers can
      // branch on `fallback`/`code`.
      let detail = resp.statusText;
      let payload: unknown = undefined;
      try {
        const body = await resp.json();
        payload = body;
        const d = body?.detail ?? body;
        if (typeof d === "string") detail = d;
        else if (typeof d?.error === "string") detail = d.error;
        else if (typeof d?.message === "string") detail = d.message;
      } catch {
        // not JSON — keep statusText
      }
      const err = new RestError(resp.status, detail, path, payload);
      // Always log the raw technical error to the console — the user-facing
      // `.message` is sanitized, but developers need the real status/path/body.
      console.error(err.technical, payload ?? "");
      throw err;
    }
    return resp;
  }

  async get<T>(path: string): Promise<T> {
    const r = await this.request(path);
    return (await r.json()) as T;
  }

  /** POST {path} with optional JSON body. 204 → undefined. */
  async post<T>(path: string, body?: unknown): Promise<T> {
    const r = await this.request(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (r.status === 204) return undefined as unknown as T;
    return (await r.json()) as T;
  }

  /** PUT {path} with JSON body. 204 → undefined. */
  async put<T>(path: string, body?: unknown): Promise<T> {
    const r = await this.request(path, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (r.status === 204) return undefined as unknown as T;
    return (await r.json()) as T;
  }

  /** PATCH {path} with JSON body. 204 → undefined. */
  async patch<T>(path: string, body?: unknown): Promise<T> {
    const r = await this.request(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (r.status === 204) return undefined as unknown as T;
    return (await r.json()) as T;
  }

  /** DELETE {path}. 204 → undefined. */
  async delete<T>(path: string): Promise<T> {
    const r = await this.request(path, { method: "DELETE" });
    if (r.status === 204) return undefined as unknown as T;
    // Some backends return an empty body with 200; tolerate that too.
    const text = await r.text();
    return (text ? (JSON.parse(text) as T) : (undefined as unknown as T));
  }

  async json<T>(path: string, init: RequestInit): Promise<T> {
    const r = await this.request(path, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init.headers ?? {}),
      },
    });
    if (r.status === 204) return undefined as unknown as T;
    return (await r.json()) as T;
  }

  async raw(path: string, init: RequestInit): Promise<Response> {
    return this.request(path, init);
  }

  /** Like raw, but returns the Response even on non-2xx (caller parses errors). */
  async rawResponse(path: string, init: RequestInit): Promise<Response> {
    const url = `${this.apiUrl}${path}`;
    return this.fetchImpl(url, { ...init, credentials: "include" });
  }
}

export class RestError extends Error {
  /** The parsed backend payload (object or string), when the body was JSON. */
  public payload?: unknown;

  constructor(
    public status: number,
    public detail: string,
    public path: string,
    payload?: unknown,
  ) {
    // `.message` is user-facing (catch sites render `e.message`), so it's a
    // human sentence — never "HTTP 403 on /path: …". The raw status/detail/path
    // stay as fields for logging and for callers that branch on status.
    super(friendlyError(status, detail, payload));
    this.name = "RestError";
    this.payload = payload;
  }

  /** A structured backend hint, when present (e.g. "manual_oauth"). Lets a
   *  caller branch on WHY a 409/400 happened instead of parsing the message. */
  get fallback(): string | undefined {
    const d = (this.payload as { detail?: unknown })?.detail ?? this.payload;
    const f = (d as { fallback?: unknown })?.fallback;
    return typeof f === "string" ? f : undefined;
  }

  get code(): string | undefined {
    const d = (this.payload as { detail?: unknown })?.detail ?? this.payload;
    const c = (d as { code?: unknown })?.code;
    return typeof c === "string" ? c : undefined;
  }

  /** The raw technical string, for console/telemetry — not for users. */
  get technical(): string {
    return `HTTP ${this.status} on ${this.path}: ${this.detail}`;
  }
}
